export function isVSDPump(pumpType: string | null): boolean {
    if (!pumpType) return false;

    return ['SplashMe VSD', '3 Speed', 'Inverter/VFD'].includes(pumpType.trim());
}

export const THREE_SPEED_LOW = 50;
export const THREE_SPEED_MEDIUM = 80;
export const THREE_SPEED_HIGH = 100;

export function isThreeSpeedPump(pumpBrand?: string | null, pumpModel?: string | null): boolean {
  return (
    (pumpBrand ?? '') === 'Pentair' && (pumpModel ?? '') === 'Whisperflow-800VS'
  ) || (
    (pumpBrand ?? '') === 'Maytronics' && (pumpModel ?? '') === 'Ecoclear-900VS'
  );
}

export const getThreeSpeedIndex = (speed?: number | null): 0 | 1 | 2 => {
  const safe = speed ?? 0;
  if (safe <= 60) return 0;
  if (safe >= 90) return 2;
  return 1;
};

export const getThreeSpeedSpeedByIndex = (index: number): number => {
  if (index === 0) return THREE_SPEED_LOW;
  if (index === 1) return THREE_SPEED_MEDIUM;
  return THREE_SPEED_HIGH;
};

export const snapThreeSpeedSpeed = (speed?: number | null): number => {
  return getThreeSpeedSpeedByIndex(getThreeSpeedIndex(speed));
};

export const getThreeSpeedShortLabel = (speed?: number | null): string => {
  const snapped = snapThreeSpeedSpeed(speed);
  if (snapped === THREE_SPEED_LOW) return 'LOW';
  if (snapped === THREE_SPEED_MEDIUM) return 'MEDIUM';
  return 'HIGH';
};

export const getThreeSpeedManualLabel = (speed?: number | null): string => {
  const snapped = snapThreeSpeedSpeed(speed);
  if (snapped === THREE_SPEED_LOW) return 'LOW SPEED';
  if (snapped === THREE_SPEED_MEDIUM) return 'MEDIUM SPEED';
  return 'HIGH SPEED';
};

// Helper function to convert WeekDay number or boolean array to array of day objects
export const getWeekdayArray = (weekDay: number | boolean[]): Array<{name: string, active: boolean}> => {
  const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  let weekdayArray: boolean[];
  
  if (Array.isArray(weekDay)) {
    // If weekDay is already a boolean array, use it directly
    weekdayArray = weekDay.length === 7 ? weekDay : Array(7).fill(false);
  } else {
    // Convert WeekDay number to binary array
    weekdayArray = Array(7).fill(false);
    for (let i = 0; i < 7; i++) {
      weekdayArray[i] = ((weekDay >> i) & 1) === 1;
    }
  }
  
  return dayNames.map((name, index) => ({
    name,
    active: weekdayArray[index]
  }));
};

// Motor Status enum - Matches MotorStatus in Android
export enum MotorStatus {
  NOT_READY = 0,
  DEFAULT_DATA_SEND = 1,
  READY = 2,
  RUNNING = 3,
  STARTING = 4,
  STOPPING = 5,
  STOPPED = 6,
  ERROR = 7,
  OVER_TEMP = 8,
  IDLE = 9
}

// Motor Status display names
export const MOTOR_STATUS_DISPLAY_NAMES: Record<MotorStatus, string> = {
  [MotorStatus.NOT_READY]: "Motor Not Ready",
  [MotorStatus.DEFAULT_DATA_SEND]: "",
  [MotorStatus.READY]: "Motor Ready",
  [MotorStatus.RUNNING]: "Motor Running",
  [MotorStatus.STARTING]: "Motor Starting",
  [MotorStatus.STOPPING]: "Motor Stopping",
  [MotorStatus.STOPPED]: "Motor Stopped",
  [MotorStatus.ERROR]: "Motor Error",
  [MotorStatus.OVER_TEMP]: "Motor Over Temp",
  [MotorStatus.IDLE]: "Motor Stopped"
};

// Helper function to check if motor is running
export function isMotorRunning(status: MotorStatus): boolean {
  return status === MotorStatus.RUNNING || status === MotorStatus.STARTING;
}

// AquaQuip 颜色模式 - Matches AquaQuipMode in Android
export const AQUAQUIP_COLORS = [
  { value: 0, name: 'RESET', colors: ['#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF'] },
  { value: 1, name: 'Blue', colors: ['#0000FF', '#0000FF', '#0000FF', '#0000FF', '#0000FF'] },
  { value: 2, name: 'Aqua', colors: ['#00FFFF', '#00FFFF', '#00FFFF', '#00FFFF', '#00FFFF'] },
  { value: 3, name: 'Green', colors: ['#008000', '#008000', '#008000', '#008000', '#008000'] },
  { value: 4, name: 'Gold', colors: ['#FFD700', '#FFD700', '#FFD700', '#FFD700', '#FFD700'] },
  { value: 5, name: 'Magenta', colors: ['#FF00FF', '#FF00FF', '#FF00FF', '#FF00FF', '#FF00FF'] },
  { value: 6, name: 'Red', colors: ['#FF0000', '#FF0000', '#FF0000', '#FF0000', '#FF0000'] },
  { value: 7, name: 'White', colors: ['#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF'] },
  { value: 8, name: 'Seaside', colors: ['#73d09f', '#1fc0a6', '#28b2bf', '#28b2bf', '#28b2bf'] },
  { value: 9, name: 'Slow Scroll', colors: ['#0000FF', '#008000', '#FF00FF', '#FF0000', '#FFFFFF'] },
  { value: 10, name: 'Rapid Scroll', colors: ['#0000FF', '#008000', '#FF00FF', '#FF0000', '#FFFFFF'] },
  { value: 11, name: 'Fireworks', colors: ['#CE2029', '#FFFCAF', '#FFE17C', '#FF664B', '#903843'] },
  { value: 12, name: 'Disco', colors: ['#0FC0FC', '#7B1DAF', '#FF2FB9', '#D4FF47', '#1B3649'] },
  { value: 13, name: 'Flash', colors: ['#ffe659', '#de3e35', '#e6e6e6', '#eb974d', '#ffe659'] },
  { value: 99, name: 'IDLE', colors: ['#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF'] }
];

// PAL Lighting 颜色模式 - Matches PalMode in Android
export const PAL_COLORS = [
  { value: 0, name: 'RESET', colors: ['#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF'] },
  { value: 1, name: 'Deep Blue', colors: ['#003B73', '#003B73', '#003B73', '#003B73', '#003B73'] },
  { value: 2, name: 'Aqua/Teal', colors: ['#1FC0A6', '#1FC0A6', '#1FC0A6', '#1FC0A6', '#1FC0A6'] },
  { value: 3, name: 'Emerald Green', colors: ['#2ECC71', '#2ECC71', '#2ECC71', '#2ECC71', '#2ECC71'] },
  { value: 4, name: 'White 4000K', colors: ['#FFEFD6', '#FFEFD6', '#FFEFD6', '#FFEFD6', '#FFEFD6'] },
  { value: 5, name: 'Soft Violet', colors: ['#BFA2DB', '#BFA2DB', '#BFA2DB', '#BFA2DB', '#BFA2DB'] },
  { value: 6, name: 'SunLover Sunset', colors: ['#FFD166', '#F8961E', '#F3722C', '#E85D04', '#9D0208'] },
  { value: 7, name: 'Northern Lights', colors: ['#7FE7C4', '#4FD1C5', '#7DD3FC', '#A5B4FC', '#C084FC'] },
  { value: 8, name: 'Candela', colors: ['#FFF8E1', '#FFE9A8', '#FFD166', '#F9A825', '#D97904'] },
  { value: 9, name: 'Enchanted Forest', colors: ['#D8F3DC', '#95D5B2', '#52B788', '#2D6A4F', '#1B4332'] },
  { value: 10, name: 'Lunar Wonder', colors: ['#FFFFFF', '#F8F7FF', '#EDEBFF', '#C8B6FF', '#9D4EDD'] },
  { value: 11, name: 'Romance', colors: ['#FF8FCF', '#FF6FB5', '#F15BB5', '#C77DFF', '#9D4EDD'] },
  { value: 12, name: 'Halloween', colors: ['#F8961E', '#F3722C', '#90BE6D', '#7B2CBF', '#5A189A'] },
  { value: 13, name: 'Symphony', colors: ['#D33B2E', '#AB0020', '#F0B0A6', '#C17565', '#FFFC7B', '#FACB33'] },
  { value: 99, name: 'IDLE', colors: ['#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF'] }
];

// SpaElectrics 颜色模式 - Matches SpaElectricsMode in Android
export const SPA_ELECTRICS_COLORS = [
  { value: 0, name: 'RESET', colors: ['#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF'] },
  { value: 1, name: 'Blue', colors: ['#0000FF', '#0000FF', '#0000FF', '#0000FF', '#0000FF'] },
  { value: 2, name: 'Magenta', colors: ['#FF00FF', '#FF00FF', '#FF00FF', '#FF00FF', '#FF00FF'] },
  { value: 3, name: 'Red', colors: ['#FF0000', '#FF0000', '#FF0000', '#FF0000', '#FF0000'] },
  { value: 4, name: 'Lime', colors: ['#32CD32', '#32CD32', '#32CD32', '#32CD32', '#32CD32'] },
  { value: 5, name: 'Green', colors: ['#008000', '#008000', '#008000', '#008000', '#008000'] },
  { value: 6, name: 'Aqua', colors: ['#00FFFF', '#00FFFF', '#00FFFF', '#00FFFF', '#00FFFF'] },
  { value: 7, name: 'White', colors: ['#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF'] },
  { value: 8, name: 'Oceanic Views', colors: ['#22639B', '#06CDE0', '#0DB3CF', '#1498BE', '#1B7EAC'] },
  { value: 9, name: 'Transcendence', colors: ['#add8e6', '#ccf0f8', '#f0f8ff', '#fff0f5', '#fddde6'] },
  { value: 10, name: 'Outback Australia', colors: ['#272bd4', '#fff3c8', '#ffdb00', '#e34915', '#693519'] },
  { value: 11, name: 'Spring Equinox', colors: ['#FF404C', '#FFCC33', '#F2F27A', '#FBA0E3', '#47AA36'] },
  { value: 12, name: 'Brightness Decrement', colors: ['#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF'] },
  { value: 99, name: 'IDLE', colors: ['#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF', '#FFFFFF'] }
];
